import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthenticationService } from '../../../services/auth/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from 'src/app/services/api/common.service';
import { NgxUiLoaderService } from "ngx-ui-loader";
import { MixpanelService } from 'src/app/mixpanel.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { GroupService } from 'src/app/services/api/group.services';
import { TeacherBookingService } from 'src/app/services/api/consumer/bookings.service';
import { PaginationComponent } from 'ngx-bootstrap/pagination';
import { PageChangedEvent } from "ngx-bootstrap/pagination/public_api";
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QuickAvailabilityUpdateComponent } from "../quick-availability-update/quick-availability-update.component";
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ArticleComponent } from '../../producer/article/article.component';
import { SchoolData } from 'src/app/models/schoolUser';

@Component({
  selector: 'app-bookings',
  standalone: true,
  imports: [PaginationComponent, CommonModule, FormsModule, ReactiveFormsModule, QuickAvailabilityUpdateComponent, NgbTooltip, ArticleComponent],
  templateUrl: './bookings.component.html',
  styleUrl: './bookings.component.scss'
})
export class BookingsComponent implements OnInit {
  @ViewChild('declineBookingModal') declineBookingModal: any;

  bookingsData: any[] = [];
  sideComponentData: any;
  selectedBookingStatus: string = 'P'; // Default status: Pending
  bookingId: any;
  responseCode: any;

  totalCount: number = 0;
  currentPage: number = 1;
  public schoolProps!: SchoolData;

  declineBookingForm: FormGroup = this.fb.group({
    responseCode: [''],
    from: [''],
    bn_id: [''],
    decline_reason: ['']
  });

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthenticationService,
    private commonService: CommonService,
    private route: ActivatedRoute,
    private ngxService: NgxUiLoaderService,
    private modalService: BsModalService,
    private toastr: ToastrService,
    private groupService: GroupService,
    private teacherBookingService: TeacherBookingService,
    public bsModalRef: BsModalRef
  ) { }

  ngOnInit(): void {
    this.getBookings();
    this.getSideComponentData();
    this.schoolProps = JSON.parse(localStorage.getItem("userData") || '{}');
  }

  // Fetch bookings
  getBookings(): void {
    this.ngxService.start();
    this.teacherBookingService.teachersBookingsAPI(this.selectedBookingStatus, this.currentPage).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.bookingsData = res.data;
          this.totalCount = parseInt(res.page_count) * 10;
        } else {
          this.toastr.error(res.message || 'Failed to load bookings');
        }
        this.ngxService.stop();
      },
      error: (err: any) => {
        console.error(err);
        this.toastr.error('Something went wrong');
        this.ngxService.stop();
      }
    });
  }

  // Fetch sidebar component data
  getSideComponentData(): void {
    this.teacherBookingService.getTeacherSideComponentAPI().subscribe({
      next: (res: any) => {
        if (res.status) {
          this.sideComponentData = res.data;
        } else {
          this.toastr.error(res.message || 'Failed to load side panel data');
        }
      },
      error: (err: any) => {
        console.error(err);
        this.toastr.error('Something went wrong');
      }
    });
  }

  // Booking status tab change
  onBookingStatusChange(event: any): void {
    this.currentPage = 1;
    this.getBookings();
  }

  // Pagination event
  pageChanged(event: PageChangedEvent): void {
    this.currentPage = event.page;
    window.scrollTo(0, 0);
    this.getBookings();
  }

  // Decline modal open
  openBookingDeclineModal(responseCode: any, bookingId: any): void {
    this.responseCode = responseCode;
    this.bookingId = bookingId;
    this.bsModalRef = this.modalService.show(this.declineBookingModal, {
      class: 'modal-md',
      backdrop: 'static'
    });
  }

  // Accept booking
  onAcceptBooking(responseCode: any, bookingId: any): void {
    const payload = {
      responseCode: 'Y' + responseCode,
      from: 'teacher',
      bn_id: bookingId
    };

    this.ngxService.start();
    this.teacherBookingService.bookingAcceptDeclineAPI(payload).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.toastr.success(res.message);
          setTimeout(() => {
            this.getBookings();
          }, 7000);
        } else {
          this.ngxService.stop();
          this.toastr.error(res.message || 'Failed to accept booking');
        }
      },
      error: (err: any) => {
        this.toastr.error(err.message || 'Failed to accept booking');
        console.error(err);
        this.ngxService.stop();
      }
    });
  }
  showDeclineMessage: boolean = false;
  // Decline booking confirm
  onConfirmBookingDecline(responseCode: any, bookingId: any): void {
    const payload = {
      responseCode: 'N' + responseCode,
      from: 'teacher',
      bn_id: bookingId,
      decline_reason: this.declineBookingForm.get('decline_reason')?.value || ''
    };

    this.ngxService.start();
    this.teacherBookingService.bookingAcceptDeclineAPI(payload).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.bsModalRef.hide();
          this.showDeclineMessage = true;
          this.toastr.success('Booking declined successfully');
          setTimeout(() => {
            this.getBookings();
          }, 7000);
          this.declineBookingForm.reset();
        } else {
          this.ngxService.stop();
          this.toastr.error(res.message || 'Failed to decline booking');
        }
      },
      error: (err: any) => {
        this.toastr.error(err.message || 'Failed to decline booking');
        console.error(err);
        this.ngxService.stop();
      }
    });
  }

  // Circular progress ring
  getSmallProfileDashOffset(availability_health: number): string {
    const r = 35;
    const circumference = 2 * Math.PI * r;
    const adjustedHealth = 100 - availability_health;
    const percent = Math.max(0, Math.min(100, adjustedHealth));
    const offset = circumference - (circumference * percent / 100);
    return offset.toFixed(3);
  }

  getSmallProfileDashArray(availability_health: number): string {
    const r = 35;
    return (2 * Math.PI * r).toFixed(3);
  }

  goToCalendar(): void {
    this.router.navigate(['teacher/calendar']);
  }
  refreshPage() {
    this.getBookings();
    this.getSideComponentData();
  }
  showTooltip = false;

  onMouseEnter() {
    this.showTooltip = true;
  }

  onMouseLeave() {
    this.showTooltip = false;
  }
  handlePageData(data: any) {
    // Store the page data for later use, e.g. pagination or display
    this.pageData = data;
    // Optionally, update the UI or trigger other logic if needed
    // Example: this.obsArray.next(data.consumer_list || []);
  }
  pageData: any;
  isArrayFormat(value: any): boolean {
    return Array.isArray(value);
  }
}
